import { useAuthStore } from '../../stores/useAuthStore';
import type {
  ObrazGetDictionariesResponse,
  ObrazGetCatalogInput,
  ObrazGetCatalogResponse,
  ObrazCatalogGetFavoritesResponse,
  ObrazCatalogGetSubscriptionsResponse,
  ObrazCatalogUserCollectionResponse,
  ObrazUserUpdateInput,
  ObrazUserUpdateResponse,
  ObrazUserGetResponse,
  ObrazAuthOtpInput,
  ObrazAuthOtpVerifyInput,
  ObrazAuthResponse,
  ObrazGetWardrobeInput,
  ObrazGetWardrobeResponse,
  ObrazWardrobeUpdateInput,
  ObrazWardrobeUpdateResponse,
  ObrazWardrobeItem,
  ObrazWardrobeItemUploadInput,
  ObrazWardrobeAddResponse,
  ObrazGetPlansResponse,
  ObrazGetPlanUsageResponse,
  ObrazInitiatePaymentResponse,
  ObrazPaymentStatusResponse,
  ObrazTrackEventInput,
  AgentAskInput,
  AgentSseEvent,
  AgentAnalyzeInput,
  AgentAnalyzeResult,
  AgentMessageInput,
  AgentMessageResponse,
  ChatDeleteOutfitInput,
  ChatDetail,
  ChatSaveOutfitInput,
  ChatSaveOutfitResponse,
  ChatSummary,
  ChatVoteInput,
  ObrazOutfitSaveInput,
  ObrazOutfitSaveResponse,
} from './api.types';
import type {
  Outfit,
  OutfitsListResponse,
  GetNextOutfitsRequest,
} from '../../types/api';

// Swagger: https://api-dev-2a7e.up.railway.app/api/swagger#/
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api-dev-2a7e.up.railway.app';

const authedFetch: typeof fetch = async (...args) => {
  const response = await globalThis.fetch(...args);
  if (response.status === 401) {
    useAuthStore.getState().logout();
  }
  return response;
};

const getHeaders = () => {
  return {
    Authorization: `Bearer ${useAuthStore.getState().token || ''}`,
    // Hard-coded for the moment, we should send this in order to
    // to get localized responses from the API (e.g. error messages)
    'Accept-Language': 'ru-RU,ru;q=0.9,en;q=0.8',
    'Content-Type': 'application/json',
  };
};

export const sendOtp = async ({ email }: ObrazAuthOtpInput): Promise<void> => {
  const response = await authedFetch(`${API_BASE_URL}/api/v1/auth/otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || 'Не удалось отправить код');
  }
};

export const verifyOtp = async ({
  email,
  code,
}: ObrazAuthOtpVerifyInput): Promise<ObrazAuthResponse> => {
  const response = await authedFetch(`${API_BASE_URL}/api/v1/auth/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || 'Неверный код');
  }

  return response.json();
};

export const getDicts = async (): Promise<ObrazGetDictionariesResponse> => {
  const response = await authedFetch(`${API_BASE_URL}/api/v1/dictionaries`);
  return response.json();
};

export const getCatalog = async ({
  page,
  take,
  textQuery,
  feedSeed,
  styles,
  gender,
}: ObrazGetCatalogInput): Promise<ObrazGetCatalogResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items`);
  url.searchParams.append('page', String(page));
  url.searchParams.append('take', String(take));

  if (textQuery) {
    url.searchParams.append('textQuery', textQuery);
  }
  if (feedSeed) {
    url.searchParams.append('feedSeed', String(feedSeed));
  }
  if (styles && styles.length > 0) {
    styles.forEach(style => url.searchParams.append('styles', style));
  }
  if (gender) {
    url.searchParams.append('gender', gender);
  }

  const response = await authedFetch(url, {
    headers: getHeaders(),
  });

  return response.json();
};

export const getFavorites = async ({
  page,
  take,
}: ObrazGetCatalogInput): Promise<ObrazCatalogGetFavoritesResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items/favorites`);
  url.searchParams.append('page', String(page));
  url.searchParams.append('take', String(take));

  const response = await authedFetch(url, {
    headers: getHeaders(),
  });

  return response.json();
};

export const getSubscriptions = async ({
  page,
  take,
}: ObrazGetCatalogInput): Promise<ObrazCatalogGetSubscriptionsResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items/subscriptions`);
  url.searchParams.append('page', String(page));
  url.searchParams.append('take', String(take));

  const response = await authedFetch(url, {
    headers: getHeaders(),
  });

  return response.json();
};

export const getUserCollection =
  async (): Promise<ObrazCatalogUserCollectionResponse> => {
    const response = await authedFetch(
      `${API_BASE_URL}/api/v1/catalog/items/collection`,
      {
        headers: getHeaders(),
      }
    );

    return response.json();
  };

export const getWardrobe = async ({
  page,
  take,
}: ObrazGetWardrobeInput): Promise<ObrazGetWardrobeResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/wardrobe/items`);
  url.searchParams.append('page', String(page));
  url.searchParams.append('take', String(take));

  const response = await authedFetch(url, {
    headers: getHeaders(),
  });

  return response.json();
};

export const addFavorite = async (id: string): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items/${id}/favorite`);

  await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
};

export const removeFavorite = async (id: string): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items/${id}/favorite`);

  await authedFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
};

export const subscribe = async (id: string): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items/${id}/subscribe`);

  await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
};

export const unsubscribe = async (id: string): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/catalog/items/${id}/subscribe`);

  await authedFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
};

export const getMe = async (): Promise<ObrazUserGetResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/users/me`);

  const response = await authedFetch(url, {
    headers: getHeaders(),
  });

  return response.json();
};

export const updateMe = async (
  data: ObrazUserUpdateInput
): Promise<ObrazUserUpdateResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/users/me`);

  const response = await authedFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return response.json();
};

export const resetAccount = async (): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/users/reset`);
  const headers = getHeaders();
  await authedFetch(url, { method: 'POST', headers });
};

export const cancelSubscriptionAtPeriodEnd = async (): Promise<void> => {
  const url = new URL(
    `${API_BASE_URL}/api/v1/users/me/subscription/cancel-at-period-end`
  );
  const response = await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      (body as { error?: { message?: string } })?.error?.message ||
        'Не удалось отменить подписку'
    );
  }
};

export const getWardrobeItem = async (
  id: string
): Promise<ObrazWardrobeItem> => {
  const url = new URL(`${API_BASE_URL}/api/v1/wardrobe/items/${id}`);

  const response = await authedFetch(url, {
    headers: getHeaders(),
  });

  return response.json();
};

const parseWardrobeUploadErrorMessage = async (
  response: Response
): Promise<string> => {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'error' in body) {
      const err = (body as { error?: { message?: string } }).error?.message;
      if (typeof err === 'string' && err.length > 0) return err;
    }
    if (body && typeof body === 'object' && 'message' in body) {
      const m = (body as { message?: string }).message;
      if (typeof m === 'string' && m.length > 0) return m;
    }
  } catch {
    /* ignore */
  }
  return response.statusText || 'Upload failed';
};

export const normalizeObrazWardrobeItem = (
  raw: unknown
): ObrazWardrobeAddResponse => {
  const data = raw as Record<string, unknown> & Partial<ObrazWardrobeItem>;
  const o = data.originImageUrls;
  const originImageUrls = Array.isArray(o)
    ? (o as string[])
    : typeof o === 'string'
      ? [o]
      : [];
  return { ...data, originImageUrls } as ObrazWardrobeAddResponse;
};

export const addWardrobeItem = async ({
  image,
  signal,
}: ObrazWardrobeItemUploadInput): Promise<ObrazWardrobeAddResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/wardrobe/items`);
  const formData = new FormData();
  formData.append('image', image);

  const response = await authedFetch(url, {
    method: 'POST',
    signal,
    headers: {
      Authorization: `Bearer ${useAuthStore.getState().token || ''}`,
      'Accept-Language': 'ru-RU,ru;q=0.9,en;q=0.8',
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await parseWardrobeUploadErrorMessage(response));
  }

  if (response.status !== 201 && response.status !== 200) {
    throw new Error(`Unexpected status ${response.status}`);
  }

  const json: unknown = await response.json();
  return normalizeObrazWardrobeItem(json);
};

export const updateWardrobeItem = async ({
  id,
  data,
}: {
  id: string;
  data: ObrazWardrobeUpdateInput;
}): Promise<ObrazWardrobeUpdateResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/wardrobe/items/${id}`);

  const response = await authedFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return response.json();
};

export const deleteWardrobeItem = async (id: string): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/wardrobe/items/${id}`);

  await authedFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
};

export const enhanceWardrobeItem = async (
  id: string
): Promise<ObrazWardrobeAddResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/wardrobe/items/${id}/enhance`);

  const response = await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(await parseWardrobeUploadErrorMessage(response));
  }

  const json: unknown = await response.json();
  return normalizeObrazWardrobeItem(json);
};

export const getPlans = async (): Promise<ObrazGetPlansResponse> => {
  const url = new URL(`${API_BASE_URL}/api/plans`);
  const response = await authedFetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  return response.json();
};

export const getPlanUsage = async (): Promise<ObrazGetPlanUsageResponse> => {
  const url = new URL(`${API_BASE_URL}/api/plans/usage`);

  const response = await authedFetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  return response.json();
};

export const initiatePayment = async (
  planType: string
): Promise<ObrazInitiatePaymentResponse> => {
  const url = new URL(`${API_BASE_URL}/api/payments/initiate`);
  const response = await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ planType }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      (body as { message?: string })?.message || 'Не удалось создать платёж'
    );
  }
  return response.json() as Promise<ObrazInitiatePaymentResponse>;
};

export const getPaymentStatus = async (
  paymentId: string
): Promise<ObrazPaymentStatusResponse> => {
  const url = new URL(`${API_BASE_URL}/api/payments/${paymentId}/status`);
  const response = await authedFetch(url, { headers: getHeaders() });
  if (!response.ok) throw new Error('Не удалось получить статус платежа');
  return response.json() as Promise<ObrazPaymentStatusResponse>;
};

export const agentMessage = async (
  input: AgentMessageInput
): Promise<AgentMessageResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/agent/message`);
  const response = await authedFetch(url.toString(), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error('Agent message failed');
  return response.json() as Promise<AgentMessageResponse>;
};

export const listChats = async (): Promise<ChatSummary[]> => {
  const url = new URL(`${API_BASE_URL}/api/v1/agent/chats`);
  const response = await authedFetch(url.toString(), { headers: getHeaders() });
  if (!response.ok) throw new Error('Failed to load chats');
  return response.json() as Promise<ChatSummary[]>;
};

export const getChat = async (id: string): Promise<ChatDetail> => {
  const url = new URL(`${API_BASE_URL}/api/v1/agent/chats/${id}`);
  const response = await authedFetch(url.toString(), { headers: getHeaders() });
  if (!response.ok) throw new Error('Failed to load chat');
  return response.json() as Promise<ChatDetail>;
};

export const deleteChat = async (id: string): Promise<void> => {
  const response = await authedFetch(
    `${API_BASE_URL}/api/v1/agent/chats/${id}`,
    { method: 'DELETE', headers: getHeaders() }
  );
  if (!response.ok) throw new Error('Failed to delete chat');
};

export const chatSaveOutfit = async (
  chatId: string,
  body: ChatSaveOutfitInput
): Promise<ChatSaveOutfitResponse> => {
  const response = await authedFetch(
    `${API_BASE_URL}/api/v1/agent/chats/${chatId}/outfit`,
    { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) }
  );
  if (!response.ok) throw new Error('Failed to save outfit');
  return response.json() as Promise<ChatSaveOutfitResponse>;
};

export const chatDeleteOutfit = async (
  chatId: string,
  body: ChatDeleteOutfitInput
): Promise<void> => {
  const response = await authedFetch(
    `${API_BASE_URL}/api/v1/agent/chats/${chatId}/outfit`,
    { method: 'DELETE', headers: getHeaders(), body: JSON.stringify(body) }
  );
  if (!response.ok) throw new Error('Failed to delete outfit');
};

export const chatVote = async (
  chatId: string,
  body: ChatVoteInput
): Promise<void> => {
  const response = await authedFetch(
    `${API_BASE_URL}/api/v1/agent/chats/${chatId}/vote`,
    { method: 'PATCH', headers: getHeaders(), body: JSON.stringify(body) }
  );
  if (!response.ok) throw new Error('Failed to save vote');
};

export const agentAnalyze = async (
  input: AgentAnalyzeInput
): Promise<AgentAnalyzeResult> => {
  const url = new URL(`${API_BASE_URL}/api/v1/agent/analyze`);
  const response = await authedFetch(url.toString(), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error('Analyze failed');
  return response.json() as Promise<AgentAnalyzeResult>;
};

export const agentAsk = async (
  input: AgentAskInput,
  onEvent: (event: AgentSseEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/agent/ask`);
  const response = await authedFetch(url.toString(), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(input),
    signal,
  });

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const raw = line.slice(6);
        try {
          // NestJS sends objects as JSON, plain strings as raw text
          const data = JSON.parse(raw) as AgentSseEvent;
          onEvent(data);
        } catch {
          onEvent(raw);
        }
      }
    }
  }
};

export const saveOutfit = async (
  input: ObrazOutfitSaveInput
): Promise<ObrazOutfitSaveResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/save`);
  const res = await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`saveOutfit failed: ${res.status}`);
  return res.json() as Promise<ObrazOutfitSaveResponse>;
};

export const deleteOutfit = async (outfitId: string): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/${outfitId}`);
  const res = await authedFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`deleteOutfit failed: ${res.status}`);
};

export const getFavoriteOutfits = async (): Promise<OutfitsListResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/favorites`);
  const response = await authedFetch(url, { headers: getHeaders() });
  if (!response.ok) {
    throw new Error(`getFavoriteOutfits failed: ${response.status}`);
  }
  return response.json() as Promise<OutfitsListResponse>;
};

export const getOutfitById = async (outfitId: string): Promise<Outfit> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/${outfitId}`);
  const response = await authedFetch(url, { headers: getHeaders() });
  if (!response.ok) {
    throw new Error(`getOutfitById failed: ${response.status}`);
  }
  return response.json() as Promise<Outfit>;
};

export const getNextOutfits = async (
  params: GetNextOutfitsRequest
): Promise<Outfit> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/next`);
  url.searchParams.set('style', params.style);
  url.searchParams.set('gender', params.gender);

  params.includeItems?.forEach(itemId =>
    url.searchParams.append('includeItems', itemId)
  );
  params.excludeItems?.forEach(itemId =>
    url.searchParams.append('excludeItems', itemId)
  );
  params.colors?.forEach(color => url.searchParams.append('colors', color));

  if (typeof params.maxPrice === 'number') {
    url.searchParams.set('maxPrice', String(params.maxPrice));
  }
  if (params.isOnlyMadeInMoscow) {
    url.searchParams.set('isOnlyMadeInMoscow', 'true');
  }

  const response = await authedFetch(url, { headers: getHeaders() });
  if (!response.ok) {
    throw new Error(`getNextOutfits failed: ${response.status}`);
  }
  return response.json() as Promise<Outfit>;
};

export const addOutfitToFavorites = async (
  outfitId: string
): Promise<Outfit> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/${outfitId}/favorites`);
  const response = await authedFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: outfitId }),
  });
  if (!response.ok) {
    throw new Error(`addOutfitToFavorites failed: ${response.status}`);
  }
  return response.json() as Promise<Outfit>;
};

export const removeOutfitFromFavorites = async (
  outfitId: string
): Promise<void> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/${outfitId}/favorites`);
  const response = await authedFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`removeOutfitFromFavorites failed: ${response.status}`);
  }
};

export const tryOnOutfit = async (outfitId: string): Promise<Blob> => {
  const url = new URL(`${API_BASE_URL}/api/v1/outfits/try-on`);
  const response = await authedFetch(url, {
    method: 'POST',
    headers: {
      ...getHeaders(),
      Accept: 'image/*',
    },
    body: JSON.stringify({ outfitId }),
  });
  if (!response.ok) {
    throw new Error(`tryOnOutfit failed: ${response.status}`);
  }
  return response.blob();
};

export const trackUserEvent = ({ type, input }: ObrazTrackEventInput): void => {
  try {
    const url = new URL(`${API_BASE_URL}/api/v1/events/track`);
    // We don't await this request, and we use keepalive to allow it to be sent even if the user navigates away from the page
    // We also catch any errors to prevent unhandled promise rejections, since this is a fire-and-forget request
    void authedFetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ type, input }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Analytics must never block app startup or user flows.
  }
};
